/**
 * CSSHelpers.ts - Advanced CSS feature helpers (Grid, Flexbox, etc.)
 * 
 * CHANGE HISTORY:
 * - 251102_1715 danielbeer: Created for Phase 3.5b - Advanced Features
 * 
 * Provides:
 * - CSS Grid layout helpers
 * - Flexbox layout helpers
 * - Spacing utilities
 * - Typography helpers
 * - Shadow generators
 * - Border utilities
 */

import type { StyleVariables } from '../types';

/**
 * Grid layout helpers
 */
export class GridHelper {
  /**
   * Create basic grid layout
   */
  static createGrid(columns: number | string, gap?: string): Partial<StyleVariables> {
    return {
      '--display': 'grid',
      '--grid-template-columns': typeof columns === 'number' 
        ? `repeat(${columns}, 1fr)` 
        : columns,
      '--gap': gap || '16px'
    };
  }

  /**
   * Create responsive grid
   */
  static createResponsiveGrid(minColumnWidth: string = '250px', gap?: string): Partial<StyleVariables> {
    return {
      '--display': 'grid',
      '--grid-template-columns': `repeat(auto-fit, minmax(${minColumnWidth}, 1fr))`,
      '--gap': gap || '16px'
    };
  }

  /**
   * Create grid with specific rows and columns
   */
  static createGridLayout(
    rows: string,
    columns: string,
    gap?: string
  ): Partial<StyleVariables> {
    return {
      '--display': 'grid',
      '--grid-template-rows': rows,
      '--grid-template-columns': columns,
      '--gap': gap || '16px'
    };
  }

  /**
   * Create grid areas layout
   */
  static createGridAreas(
    areas: string[],
    columns: string,
    rows?: string
  ): Partial<StyleVariables> {
    return {
      '--display': 'grid',
      '--grid-template-areas': areas.map(a => `"${a}"`).join(' '),
      '--grid-template-columns': columns,
      '--grid-template-rows': rows || 'auto'
    };
  }

  /**
   * Grid item placement
   */
  static placeItem(
    row?: string,
    column?: string,
    area?: string
  ): Partial<StyleVariables> {
    const vars: Partial<StyleVariables> = {};
    
    if (row) vars['--grid-row'] = row;
    if (column) vars['--grid-column'] = column;
    if (area) vars['--grid-area'] = area;
    
    return vars;
  }
}

/**
 * Flexbox layout helpers
 */
export class FlexHelper {
  /**
   * Create basic flex container
   */
  static createFlex(
    direction: 'row' | 'column' | 'row-reverse' | 'column-reverse' = 'row',
    gap?: string
  ): Partial<StyleVariables> {
    return {
      '--display': 'flex',
      '--flex-direction': direction,
      '--gap': gap || '8px'
    };
  }

  /**
   * Create flex container with alignment
   */
  static createFlexAligned(
    direction: 'row' | 'column' = 'row',
    justify: string = 'flex-start',
    align: string = 'stretch',
    gap?: string
  ): Partial<StyleVariables> {
    return {
      '--display': 'flex',
      '--flex-direction': direction,
      '--justify-content': justify,
      '--align-items': align,
      '--gap': gap || '8px'
    };
  }

  /**
   * Create centered flex container
   */
  static createFlexCenter(direction: 'row' | 'column' = 'row'): Partial<StyleVariables> {
    return {
      '--display': 'flex',
      '--flex-direction': direction,
      '--justify-content': 'center',
      '--align-items': 'center'
    };
  }

  /**
   * Create space-between flex container
   */
  static createFlexSpaceBetween(
    direction: 'row' | 'column' = 'row'
  ): Partial<StyleVariables> {
    return {
      '--display': 'flex',
      '--flex-direction': direction,
      '--justify-content': 'space-between',
      '--align-items': 'center'
    };
  }

  /**
   * Flex item properties
   */
  static flexItem(
    grow: number = 0,
    shrink: number = 1,
    basis: string = 'auto'
  ): Partial<StyleVariables> {
    return {
      '--flex-grow': String(grow),
      '--flex-shrink': String(shrink),
      '--flex-basis': basis
    };
  }

  /**
   * Flex wrap
   */
  static flexWrap(wrap: 'nowrap' | 'wrap' | 'wrap-reverse' = 'wrap'): Partial<StyleVariables> {
    return {
      '--flex-wrap': wrap
    };
  }
}

/**
 * Spacing utilities
 */
export class SpacingHelper {
  /**
   * Create padding
   */
  static padding(
    top?: string,
    right?: string,
    bottom?: string,
    left?: string
  ): Partial<StyleVariables> {
    if (!right && !bottom && !left) {
      return { '--padding': top || '0' };
    }
    
    const values = [top, right, bottom, left]
      .filter(v => v !== undefined)
      .join(' ');
    
    return { '--padding': values };
  }

  /**
   * Create margin
   */
  static margin(
    top?: string,
    right?: string,
    bottom?: string,
    left?: string
  ): Partial<StyleVariables> {
    if (!right && !bottom && !left) {
      return { '--margin': top || '0' };
    }
    
    const values = [top, right, bottom, left]
      .filter(v => v !== undefined)
      .join(' ');
    
    return { '--margin': values };
  }

  /**
   * Spacing scale (8px base)
   */
  static scale(multiplier: number): string {
    return `${multiplier * 8}px`;
  }

  /**
   * Create spacing variables
   */
  static createSpacingScale(): Partial<StyleVariables> {
    return {
      '--spacing-xs': '4px',
      '--spacing-sm': '8px',
      '--spacing-md': '16px',
      '--spacing-lg': '24px',
      '--spacing-xl': '32px',
      '--spacing-2xl': '48px',
      '--spacing-3xl': '64px'
    };
  }
}

/**
 * Typography helpers
 */
export class TypographyHelper {
  /**
   * Create font variables
   */
  static createFont(
    family?: string,
    size?: string,
    weight?: string | number,
    lineHeight?: string
  ): Partial<StyleVariables> {
    const vars: Partial<StyleVariables> = {};
    
    if (family) vars['--font-family'] = family;
    if (size) vars['--font-size'] = size;
    if (weight) vars['--font-weight'] = String(weight);
    if (lineHeight) vars['--line-height'] = lineHeight;
    
    return vars;
  }

  /**
   * Typography scale
   */
  static createTypographyScale(): Partial<StyleVariables> {
    return {
      '--font-size-xs': '12px',
      '--font-size-sm': '14px',
      '--font-size-base': '16px',
      '--font-size-lg': '18px',
      '--font-size-xl': '20px',
      '--font-size-2xl': '24px',
      '--font-size-3xl': '30px',
      '--font-size-4xl': '36px',
      '--font-size-5xl': '48px',
      
      '--line-height-tight': '1.25',
      '--line-height-normal': '1.5',
      '--line-height-relaxed': '1.75',
      
      '--font-weight-light': '300',
      '--font-weight-normal': '400',
      '--font-weight-medium': '500',
      '--font-weight-semibold': '600',
      '--font-weight-bold': '700'
    };
  }

  /**
   * Text alignment
   */
  static textAlign(align: 'left' | 'center' | 'right' | 'justify'): Partial<StyleVariables> {
    return { '--text-align': align };
  }

  /**
   * Text decoration
   */
  static textDecoration(
    line: 'none' | 'underline' | 'overline' | 'line-through',
    style?: 'solid' | 'double' | 'dotted' | 'dashed' | 'wavy',
    color?: string
  ): Partial<StyleVariables> {
    let value = line;
    if (style) value += ` ${style}`;
    if (color) value += ` ${color}`;
    
    return { '--text-decoration': value };
  }

  /**
   * Text transform
   */
  static textTransform(
    transform: 'none' | 'uppercase' | 'lowercase' | 'capitalize'
  ): Partial<StyleVariables> {
    return { '--text-transform': transform };
  }
}

/**
 * Shadow generators
 */
export class ShadowHelper {
  /**
   * Create box shadow
   */
  static boxShadow(
    x: string,
    y: string,
    blur: string,
    spread: string,
    color: string,
    inset: boolean = false
  ): Partial<StyleVariables> {
    const shadow = `${inset ? 'inset ' : ''}${x} ${y} ${blur} ${spread} ${color}`;
    return { '--box-shadow': shadow };
  }

  /**
   * Elevation shadows (Material Design inspired)
   */
  static elevation(level: 1 | 2 | 3 | 4 | 5): Partial<StyleVariables> {
    const shadows = {
      1: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
      2: '0 3px 6px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.12)',
      3: '0 10px 20px rgba(0,0,0,0.15), 0 3px 6px rgba(0,0,0,0.10)',
      4: '0 15px 25px rgba(0,0,0,0.15), 0 5px 10px rgba(0,0,0,0.05)',
      5: '0 20px 40px rgba(0,0,0,0.2)'
    };
    
    return { '--box-shadow': shadows[level] };
  }

  /**
   * Text shadow
   */
  static textShadow(
    x: string,
    y: string,
    blur: string,
    color: string
  ): Partial<StyleVariables> {
    return { '--text-shadow': `${x} ${y} ${blur} ${color}` };
  }

  /**
   * Glow effect
   */
  static glow(color: string, size: string = '10px'): Partial<StyleVariables> {
    return { '--box-shadow': `0 0 ${size} ${color}` };
  }
}

/**
 * Border utilities
 */
export class BorderHelper {
  /**
   * Create border
   */
  static border(
    width: string = '1px',
    style: string = 'solid',
    color: string = '#000'
  ): Partial<StyleVariables> {
    return { '--border': `${width} ${style} ${color}` };
  }

  /**
   * Border radius
   */
  static borderRadius(
    radius: string | number,
    topLeft?: string,
    topRight?: string,
    bottomRight?: string,
    bottomLeft?: string
  ): Partial<StyleVariables> {
    if (!topLeft && !topRight && !bottomRight && !bottomLeft) {
      return { '--border-radius': typeof radius === 'number' ? `${radius}px` : radius };
    }
    
    const values = [topLeft, topRight, bottomRight, bottomLeft]
      .filter(v => v !== undefined)
      .join(' ');
    
    return { '--border-radius': values };
  }

  /**
   * Rounded corners presets
   */
  static rounded(size: 'none' | 'sm' | 'md' | 'lg' | 'full'): Partial<StyleVariables> {
    const sizes = {
      none: '0',
      sm: '4px',
      md: '8px',
      lg: '16px',
      full: '9999px'
    };
    
    return { '--border-radius': sizes[size] };
  }

  /**
   * Outline
   */
  static outline(
    width: string = '2px',
    style: string = 'solid',
    color: string = '#000',
    offset?: string
  ): Partial<StyleVariables> {
    const vars: Partial<StyleVariables> = {
      '--outline': `${width} ${style} ${color}`
    };
    
    if (offset) {
      vars['--outline-offset'] = offset;
    }
    
    return vars;
  }
}

/**
 * Position helpers
 */
export class PositionHelper {
  /**
   * Create position
   */
  static position(
    type: 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky',
    top?: string,
    right?: string,
    bottom?: string,
    left?: string
  ): Partial<StyleVariables> {
    const vars: Partial<StyleVariables> = {
      '--position': type
    };
    
    if (top) vars['--top'] = top;
    if (right) vars['--right'] = right;
    if (bottom) vars['--bottom'] = bottom;
    if (left) vars['--left'] = left;
    
    return vars;
  }

  /**
   * Center absolutely
   */
  static centerAbsolute(): Partial<StyleVariables> {
    return {
      '--position': 'absolute',
      '--top': '50%',
      '--left': '50%',
      '--transform': 'translate(-50%, -50%)'
    };
  }

  /**
   * Full cover
   */
  static fullCover(): Partial<StyleVariables> {
    return {
      '--position': 'absolute',
      '--top': '0',
      '--right': '0',
      '--bottom': '0',
      '--left': '0'
    };
  }
}

/**
 * Size helpers
 */
export class SizeHelper {
  /**
   * Create size
   */
  static size(width?: string, height?: string): Partial<StyleVariables> {
    const vars: Partial<StyleVariables> = {};
    
    if (width) vars['--width'] = width;
    if (height) vars['--height'] = height;
    
    return vars;
  }

  /**
   * Square size
   */
  static square(size: string): Partial<StyleVariables> {
    return {
      '--width': size,
      '--height': size
    };
  }

  /**
   * Min/max sizes
   */
  static constraints(
    minWidth?: string,
    maxWidth?: string,
    minHeight?: string,
    maxHeight?: string
  ): Partial<StyleVariables> {
    const vars: Partial<StyleVariables> = {};
    
    if (minWidth) vars['--min-width'] = minWidth;
    if (maxWidth) vars['--max-width'] = maxWidth;
    if (minHeight) vars['--min-height'] = minHeight;
    if (maxHeight) vars['--max-height'] = maxHeight;
    
    return vars;
  }

  /**
   * Aspect ratio
   */
  static aspectRatio(ratio: string): Partial<StyleVariables> {
    return { '--aspect-ratio': ratio };
  }
}

/**
 * Combined CSS helpers
 */
export const CSSHelpers = {
  grid: GridHelper,
  flex: FlexHelper,
  spacing: SpacingHelper,
  typography: TypographyHelper,
  shadow: ShadowHelper,
  border: BorderHelper,
  position: PositionHelper,
  size: SizeHelper
};
